import { useEffect, useState } from 'react'
import { Tooltip } from 'react-tooltip'
import cn from 'clsx'
import { Loader, Typography } from '@corecomponents/ui'
import useGraphAnalytics from '@api/hooks/ambassdor/useGraphAnalytics'
import { NOTIFICATION_TYPE, PROFILE_STAGE, PROGRAM_TYPE } from '@utils/enum'
import { Notification } from '@utils/utilities'
import { TILE_NO, TILE_TYPE } from '@utils/constants'
import LineChart from '../LineChart'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import s from './StatisticsData.module.css'
import {
  CAP_INFO_ID,
  INVALID_RANGE,
  PAYOUT_CAP_INFO,
  PAYOUTS_TILES,
  REVENUE_TILES,
  SAATHI_PAYOUT_DATA,
  SAATHI_REVENUSE_DATA,
  SAATHI_SALES_DATA,
  SALES_TILES,
  TOP,
} from '@utils/translation'
import { useAmbassadorContext } from '@context/context'
import useSaathiAnalytics from '@api/hooks/useSaathiAnalytics'

type saathiLead = {
  startDate: string
  endDate: string
  orgId: string
  type: string
}

const StatisticsData = ({
  startDate,
  endDate,
  applyYearlyMetrics,
  cameFrom,
}: {
  startDate: string
  endDate: string
  applyYearlyMetrics: boolean
  cameFrom?: string
}) => {
  const { myProfileValue } = useAmbassadorContext()
  const [graphApiData, setGraphApiData] = useState<any>({})
  const [saathiLeadsData, setSaathiLeadsData] = useState<any>({})

  const {
    data: graphiDetails,
    error: errorMsg,
    isFetching,
  } = useGraphAnalytics(graphApiData)

  const { data: saathiDetails, error: errMsg } =
    useSaathiAnalytics(saathiLeadsData)

  const handleRevenue = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi
      ? saathiDetails?.revenue || 0
      : graphiDetails?.totalRevenue || 0
  }

  const handlePayouts = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi
      ? saathiDetails?.payout || 0
      : graphiDetails?.totalPayouts || 0
  }

  const handleRevenueName = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi ? TILE_TYPE.Accepted : TILE_TYPE.Revenue
  }

  const handleConversionName = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi ? TILE_TYPE.Conversion : TILE_TYPE.Sales
  }

  const handleSuccessName = () => {
    const isSaathi =
      myProfileValue?.programType === PROFILE_STAGE.SAATHI_PROGRAM
    return isSaathi
      ? saathiDetails?.convertedLeadCount || 0
      : graphiDetails?.totalSales || 0
  }

  const formatCurrency = (amount = 0) => {
    return `₹${Number(amount).toLocaleString('en-IN')}`
  }

  const StatisticsDataType = [
    {
      name: handleRevenueName(),
      amount: formatCurrency(handleRevenue()),
    },
    {
      name: handleConversionName(),
      amount: handleSuccessName(),
    },
    {
      name: TILE_TYPE.Payouts,
      amount: formatCurrency(handlePayouts()),
    },
  ]

  const dotsColorClassName = (index: number) =>
    cn(s.tile_color_dots, {
      ['bg-[#EDB84F]']: index === 0,
      ['bg-[#D2CCFF]']: index === 1,
      ['bg-[#2B91C9]']: index === 2,
    })

  const getSatusForInfoIcon = (index: number, amount: number) => {
    return (
      index === 2 &&
      myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM &&
      amount === myProfileValue?.totalPayoutCap
    )
  }

  useEffect(() => {
    if (myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM) {
      setGraphApiData({
        startDate: startDate,
        endDate: endDate,
        ...(applyYearlyMetrics && { applyYearlyMetrics }),
      })
    } else {
      setSaathiLeadsData({
        startDate: startDate,
        endDate: endDate,
        orgId: process.env.REACT_APP_PP_PW_ORGANIZATION_ID,
        type: 'SAATHI',
      })
    }
  }, [startDate, endDate])

  useEffect(() => {
    if (errorMsg) {
      const getErrorMessage = errorMsg.message || INVALID_RANGE
      Notification(getErrorMessage, NOTIFICATION_TYPE.ERROR)
    }
  }, [errorMsg])

  const filteredStats =
    cameFrom === PROGRAM_TYPE.CSC_PROGRAM
      ? StatisticsDataType.slice(0, 2)
      : StatisticsDataType

  return (
    <div>
      <div className={s.tile_section}>
        {filteredStats.map((items: any, index: number) => {
          return (
            <div
              className={s.tile_box}
              style={{ boxShadow: '0px 1px 8px 0px rgba(0, 0, 0, 0.08)' }}
            >
              <div className={s.tile_info}>
                <div className={s.tile_title}>
                  <div className={dotsColorClassName(index)}></div>
                  <Typography variant={'regular'} weight={500}>
                    <span className={s.tile_name}>{items.name}</span>
                  </Typography>
                </div>

                <div className={s.tile_info_div}>
                  <InformationCircleIcon
                    className={s.tile_info_circle}
                    id={
                      index === 0
                        ? TILE_NO.TILE_NO_1
                        : index === 1
                        ? TILE_NO.TILE_NO_2
                        : index === 2
                        ? TILE_NO.TILE_NO_3
                        : ''
                    }
                  />
                </div>
              </div>
              <div className={s.tile_amount_info}>
                <Typography weight={500}>
                  <span className={s.tile_amount}>{items.amount}</span>
                </Typography>

                {getSatusForInfoIcon(index, items?.amount) && (
                  <InformationCircleIcon
                    id={CAP_INFO_ID}
                    height={24}
                    width={24}
                  />
                )}
              </div>
            </div>
          )
        })}
      </div>
      {myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM && (
        <div className={s.graph_section}>
          <div className={s.inner_section}>
            {isFetching ? (
              <div className={s.graph_loader}>
                <Loader color={`#5a4bda`} scale={3} />
              </div>
            ) : (
              <LineChart
                graphiDetails={graphiDetails?.analytics}
                startDate={startDate}
                endDate={endDate}
              />
            )}
          </div>
        </div>
      )}
      <Tooltip
        anchorId={TILE_NO.TILE_NO_1}
        place={TOP}
        className={s.tool_tip}
        content={
          myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM
            ? REVENUE_TILES
            : SAATHI_REVENUSE_DATA
        }
      />
      <Tooltip
        anchorId={TILE_NO.TILE_NO_2}
        place={TOP}
        className={s.tool_tip}
        content={
          myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM
            ? SALES_TILES
            : SAATHI_SALES_DATA
        }
      />
      <Tooltip
        anchorId={TILE_NO.TILE_NO_3}
        place={TOP}
        className={s.tool_tip}
        content={
          myProfileValue?.programType !== PROFILE_STAGE.SAATHI_PROGRAM
            ? PAYOUTS_TILES
            : SAATHI_PAYOUT_DATA
        }
      />
      <Tooltip
        anchorId={CAP_INFO_ID}
        place={TOP}
        className={s.tool_tip}
        content={PAYOUT_CAP_INFO}
      />
    </div>
  )
}

export default StatisticsData
